import { Container, DisplayObject, Graphics, interaction, mesh, Sprite } from 'pixi.js';
import Viewport from 'pixi-viewport';
import NineSlicePlane = mesh.NineSlicePlane;

const defaultOptions: ScrollBoxOptions = {
    boxWidth: 100,
    boxHeight: 100,
    dragScroll: true,
    stopPropagation: true,
    underflow: 'top-left',
    passiveWheel: false,
    clampWheel: true,
    horizontalScrollbarOffset: { x: 0, y: 0 },
    verticalScrollbarOffset: { x: 0, y: 0 },
    horizontalScrollbarMargin: { left: 0, right: 0 },
    verticalScrollbarMargin: { top: 0, bottom: 0 },
    horizontalOffset: { left: 0, right: 0 },
    verticalOffset: { top: 0, bottom: 0 },
    overflow: 'auto',
    overflowX: 'auto',
    overflowY: 'auto',
    horizontalHandle: undefined,
    scaleHorizontalHandle: true,
    horizontalPath: undefined,
    verticalHandle: undefined,
    scaleVerticalHandle: true,
    verticalPath: undefined,
};
type OverflowScrollType = 'none' | 'scroll' | 'hidden' | 'auto';
type UnderflowType = 'center' | 'top' | 'left' | 'right' | 'bottom' | string;

export type ScrollBoxOptions = {
    boxHeight: number;
    boxWidth: number;
    dragScroll: boolean;
    stopPropagation: boolean;
    underflow: UnderflowType;
    passiveWheel: boolean;
    clampWheel: boolean;
    overflowX: OverflowScrollType;
    overflowY: OverflowScrollType;
    overflow: OverflowScrollType;
    verticalPath: Sprite | NineSlicePlane;
    verticalHandle: Sprite | NineSlicePlane;
    scaleVerticalHandle: boolean;
    horizontalPath: Sprite | NineSlicePlane;
    horizontalHandle: Sprite | NineSlicePlane;
    scaleHorizontalHandle: boolean;
    horizontalScrollbarOffset: { x: number; y: number };
    verticalScrollbarOffset: { x: number; y: number };
    horizontalScrollbarMargin: { left: number; right: number };
    verticalScrollbarMargin: { top: number; bottom: number };
    horizontalOffset: { left: number; right: number };
    verticalOffset: { top: number; bottom: number };
} & Viewport.Options;

type ScrollBoxResizeOptions = {
    boxWidth?: number;
    boxHeight?: number;
    scrollWidth?: number;
    scrollHeight?: number;
};

export enum ScrollBoxEvent {
    VISIBILITY_CHANGED = 'visibilityChanged',
    MOVED = 'moved',
}

/**
 * Scrollable content box using PIXI-viewport.
 */
export class PIXIScrollBox extends Container {
    protected readonly _maskContent: Graphics;
    protected _disabled: boolean;
    protected _isScrollbarVertical: boolean;
    protected _isScrollbarHorizontal: boolean;
    protected _scrollWidth: number;
    protected _scrollHeight: number;

    public options: ScrollBoxOptions;
    public content: Viewport;
    public scrollbar: Container;
    public scrollbarTop: number;
    public scrollbarLeft: number;
    public scrollbarHeight: number;
    public scrollbarWidth: number;
    public ease: any;
    public pointerDown: any;
    public verticalPath: Sprite | NineSlicePlane;
    public verticalHandle: Sprite | NineSlicePlane;
    public horizontalPath: Sprite | NineSlicePlane;
    public horizontalHandle: Sprite | NineSlicePlane;

    constructor(options?: Partial<ScrollBoxOptions>) {
        super();

        options = options ?? {};

        options.horizontalScrollbarOffset = {
            ...defaultOptions.horizontalScrollbarOffset,
            ...options.horizontalScrollbarOffset,
        };

        options.verticalScrollbarOffset = {
            ...defaultOptions.verticalScrollbarOffset,
            ...options.verticalScrollbarOffset,
        };

        options.horizontalScrollbarMargin = {
            ...defaultOptions.horizontalScrollbarMargin,
            ...options.horizontalScrollbarMargin,
        };

        options.verticalScrollbarMargin = {
            ...defaultOptions.verticalScrollbarMargin,
            ...options.verticalScrollbarMargin,
        };

        this.options = { ...defaultOptions, ...options };

        if (options.overflow) {
            this.options.overflowX = this.options.overflowY = options.overflow;
        }

        this.content = this.addChild(
            new Viewport({
                screenWidth: this.options.boxWidth,
                screenHeight: this.options.boxHeight,
                ...this.options,
            })
        );

        this.content.decelerate();

        this.content.on('moved', this.moved, this);

        // For some reason the drag component will emit both `moved` and `wheel-scroll` events at the same time. However
        // the `moved` event is also dispatched by the wheel component before the drag component has updated the
        // position of the content. This causes issues as the `moved` listener is sometimes called before the content
        // position is updated. Listening for `wheel-scroll` as well resolves this issue however `this.moved` is called
        // excessively.
        this.content.on('wheel-scroll', this.moved, this);

        this.scrollbar = this.addChild(new Container());

        if (this.options.horizontalPath) {
            this.horizontalPath = this.options.horizontalPath;
            this.scrollbar.addChild(this.horizontalPath);
        }

        if (this.options.verticalPath) {
            this.verticalPath = this.options.verticalPath;
            this.scrollbar.addChild(this.verticalPath);
        }

        if (this.options.horizontalHandle) {
            this.horizontalHandle = this.options.horizontalHandle;
            this.scrollbar.addChild(this.horizontalHandle);
        }

        if (this.options.verticalHandle) {
            this.verticalHandle = this.options.verticalHandle;
            this.scrollbar.addChild(this.verticalHandle);
        }

        this.scrollbar.interactive = true;
        this.interactive = true;
        this.scrollbar.on('pointerdown', this.scrollbarDown, this);

        this.on('pointermove', this.dragMove, this);
        this.on('pointerup', this.dragEnd, this);
        this.on('pointercancel', this.dragEnd, this);
        this.on('pointerupoutside', this.dragEnd, this);

        this._maskContent = this.addChild(new Graphics());
        this.update();
    }

    /**
     * Disable the scrollbox (if set to true this will also remove the mask).
     */
    get disable() {
        return this._disabled;
    }
    set disable(value) {
        if (this._disabled !== value) {
            this._disabled = value;
            this.update();
        }
    }

    /**
     * Call stopPropagation on any events that impact scrollbox.
     */
    get stopPropagation() {
        return this.options.stopPropagation;
    }
    set stopPropagation(value) {
        this.options.stopPropagation = value;
    }

    /**
     * User may drag the content area to scroll content.
     */
    get dragScroll() {
        return this.options.dragScroll;
    }
    set dragScroll(value) {
        this.options.dragScroll = value;
        if (value) {
            this.content.drag();
        } else {
            this.content.removePlugin('drag');
        }
        this.update();
    }

    /**
     * Width of scrollbox including the scrollbar (if visible)- this changes the size and not the scale of the box.
     */
    get boxWidth() {
        return this.options.boxWidth;
    }
    set boxWidth(value) {
        this.options.boxWidth = value;
        this.content.screenWidth = value;
        this.update();
    }

    /**
     * Height of scrollbox including the scrollbar (if visible) - this changes the size and not the scale of the box.
     */
    get boxHeight() {
        return this.options.boxHeight;
    }
    set boxHeight(value) {
        this.options.boxHeight = value;
        this.content.screenHeight = value;
        this.update();
    }

    /**
     * Sets overflowX and overflowY to (scroll, hidden, auto) changing whether the scrollbar is shown.
     * scroll = always show scrollbar
     * hidden = hide overflow and do not show scrollbar
     * auto = if content is larger than box size, then show scrollbar
     */
    get overflow() {
        return this.options.overflow;
    }
    set overflow(value) {
        this.options.overflow = value;
        this.options.overflowX = value;
        this.options.overflowY = value;
        this.update();
    }

    /**
     * Sets overflowX to (scroll, hidden, auto) changing whether the scrollbar is shown.
     * scroll = always show scrollbar
     * hidden = hide overflow and do not show scrollbar
     * auto = if content is larger than box size, then show scrollbar
     */
    get overflowX() {
        return this.options.overflowX;
    }
    set overflowX(value) {
        this.options.overflowX = value;
        this.update();
    }

    /**
     * Sets overflowY to (scroll, hidden, auto) changing whether the scrollbar is shown.
     * scroll = always show scrollbar
     * hidden = hide overflow and do not show scrollbar
     * auto = if content is larger than box size, then show scrollbar
     */
    get overflowY() {
        return this.options.overflowY;
    }
    set overflowY(value) {
        this.options.overflowY = value;
        this.update();
    }

    /**
     * Return true if the vertical scrollbar is visible otherwise false.
     */
    get isScrollbarVertical() {
        return this._isScrollbarVertical;
    }

    /**
     * Return true if the horizontal scrollbar is visible otherwise false.
     */
    get isScrollbarHorizontal() {
        return this._isScrollbarHorizontal;
    }

    /**
     * Top coordinate of scrollbar.
     */
    get scrollTop() {
        return this.content.top;
    }

    /**
     * Left coordinate of scrollbar.
     */
    get scrollLeft() {
        return this.content.left;
    }

    /**
     * Width of content area.
     * If not set then it uses content.width to calculate width.
     */
    get scrollWidth() {
        return this._scrollWidth || this.content.worldWidth;
    }
    set scrollWidth(value: number) {
        this.content.worldWidth = this._scrollWidth = value;
    }

    /**
     * Height of content area.
     * If not set then it uses content.height to calculate height.
     */
    get scrollHeight() {
        return this._scrollHeight || this.content.worldHeight;
    }
    set scrollHeight(value: number) {
        this.content.worldHeight = this._scrollHeight = value;
    }

    /**
     * Called while content is moving. Update the scrollbar and visible contents.
     */
    private moved() {
        this.drawScrollbars();
        this.updateVisibleChildren();
        this.emit(ScrollBoxEvent.MOVED);
    }

    public setScrollPosition(x: number, y: number) {
        this.content.left = x;
        this.content.top = y;
        this.moved();
    }

    /**
     * Draws scrollbars.
     */
    private drawScrollbars() {
        if (this.horizontalHandle === undefined && this.verticalHandle === undefined) return;
        this._isScrollbarHorizontal =
            this.overflowX === 'scroll'
                ? true
                : ['hidden', 'none'].indexOf(this.overflowX) !== -1
                    ? false
                    : this.scrollWidth > this.options.boxWidth;
        this._isScrollbarVertical =
            this.overflowY === 'scroll'
                ? true
                : ['hidden', 'none'].indexOf(this.overflowY) !== -1
                    ? false
                    : this.scrollHeight > this.options.boxHeight;

        this.scrollbarTop =
            (this.content.top / (this.scrollHeight - this.boxHeight)) * (this.boxHeight - this.verticalHandle.height);
        this.scrollbarTop = this.scrollbarTop < 0 ? 0 : this.scrollbarTop;
        if (this.options.scaleVerticalHandle) {
            this.scrollbarHeight = (this.boxHeight / this.scrollHeight) * this.boxHeight;
            this.scrollbarHeight =
                this.scrollbarTop + this.scrollbarHeight > this.boxHeight
                    ? this.boxHeight - this.scrollbarTop
                    : this.scrollbarHeight;
        } else {
            this.scrollbarHeight = this.verticalHandle.height; // Or fixed value?
        }
        this.scrollbarLeft = (this.content.left / this.scrollWidth) * this.boxWidth;
        this.scrollbarLeft = this.scrollbarLeft < 0 ? 0 : this.scrollbarLeft;
        this.scrollbarWidth = (this.boxWidth / this.scrollWidth) * this.boxWidth;
        this.scrollbarWidth =
            this.scrollbarWidth + this.scrollbarLeft > this.boxWidth
                ? this.boxWidth - this.scrollbarLeft
                : this.scrollbarWidth;

        if (this.isScrollbarVertical && this.verticalPath) {
            this.verticalPath.x = this.boxWidth + this.options.verticalScrollbarOffset.x;
            this.verticalPath.y = this.options.verticalScrollbarOffset.y + this.options.verticalScrollbarMargin.top;
            this.verticalPath.height =
                this.boxHeight - this.options.verticalScrollbarMargin.bottom - this.options.verticalScrollbarMargin.top;
        }
        if (this.isScrollbarHorizontal && this.horizontalPath) {
            this.horizontalPath.x =
                this.options.horizontalScrollbarOffset.x + this.options.horizontalScrollbarMargin.left;
            this.horizontalPath.y = this.boxHeight + this.options.horizontalScrollbarOffset.y;
            this.horizontalPath.width =
                this.boxWidth -
                this.options.horizontalScrollbarMargin.right -
                this.options.horizontalScrollbarMargin.left;
        }
        if (this.isScrollbarVertical && this.verticalHandle) {
            this.verticalHandle.x = this.boxWidth + this.options.verticalScrollbarOffset.x;
            this.verticalHandle.y = this.scrollbarTop + this.options.verticalScrollbarOffset.y;
            if (this.options.scaleVerticalHandle) {
                this.verticalHandle.height = this.scrollbarHeight;
            }
        }
        if (this.isScrollbarHorizontal && this.horizontalHandle) {
            this.horizontalHandle.x = this.scrollbarLeft + this.options.horizontalScrollbarOffset.x;
            this.horizontalHandle.y = this.boxHeight + this.options.horizontalScrollbarOffset.y;
            if (this.options.scaleHorizontalHandle) {
                this.horizontalHandle.width = this.scrollbarWidth;
            }
        }

        if (this.verticalHandle) this.verticalHandle.visible = this.isScrollbarVertical;
        if (this.verticalPath) this.verticalPath.visible = this.isScrollbarVertical;

        if (this.horizontalHandle) this.horizontalHandle.visible = this.isScrollbarVertical;
        if (this.horizontalPath) this.horizontalPath.visible = this.isScrollbarVertical;
    }

    /**
     * Draws mask layer.
     */
    private drawMask() {
        this._maskContent
            .beginFill(0)
            .drawRect(0, 0, this.boxWidth, this.boxHeight)
            .endFill();
        this.content.mask = this._maskContent;
    }

    /**
     * Called when scrollbox content changes.
     */
    public update() {
        this.content.mask = null;
        this._maskContent.clear();
        if (!this._disabled) {
            this.drawScrollbars();
            this.updateVisibleChildren();
            this.drawMask();
            if (this.options.dragScroll) {
                const direction =
                    this.isScrollbarHorizontal && this.isScrollbarVertical
                        ? 'all'
                        : this.isScrollbarHorizontal
                            ? 'x'
                            : 'y';
                if (direction !== null) {
                    this.content
                        .drag({
                            clampWheel: this.options.clampWheel,
                            direction,
                        })
                        .clamp({
                            direction,
                            underflow: this.options.underflow,
                        });
                }
            }
        }
    }

    private scrollbarDown(e: interaction.InteractionEvent) {
        const local = this.toLocal(e.data.global);
        if (this.isScrollbarHorizontal) {
            if (local.y > this.boxHeight) {
                if (local.x >= this.scrollbarLeft && local.x <= this.scrollbarLeft + this.scrollbarWidth) {
                    this.pointerDown = { type: 'horizontal', last: local };
                } else {
                    if (local.x > this.scrollbarLeft) {
                        this.content.left += this.content.worldScreenWidth;
                        this.moved();
                    } else {
                        this.content.left -= this.content.worldScreenWidth;
                        this.moved();
                    }
                }
                if (this.options.stopPropagation) {
                    e.stopPropagation();
                }
                return;
            }
        }
        if (this.isScrollbarVertical) {
            if (local.x > this.boxWidth) {
                if (local.y >= this.scrollbarTop && local.y <= this.scrollbarTop + this.scrollbarHeight) {
                    this.pointerDown = { type: 'vertical', last: local };
                } else {
                    if (local.y > this.scrollbarTop) {
                        this.content.top += this.content.worldScreenHeight;
                        this.moved();
                    } else {
                        this.content.top -= this.content.worldScreenHeight;
                        this.moved();
                    }
                }
                if (this.options.stopPropagation) {
                    e.stopPropagation();
                }
                return;
            }
        }
    }

    /**
     * Handle pointer move on content or scrollbar
     */
    private dragMove(e: interaction.InteractionEvent) {
        if (this.pointerDown) {
            if (this.pointerDown.type === 'horizontal') {
                const local = this.toLocal(e.data.global);
                this.content.left += ((local.x - this.pointerDown.last.x) / this.boxWidth) * this.scrollWidth;
                this.pointerDown.last = local;
            } else if (this.pointerDown.type === 'vertical') {
                const local = this.toLocal(e.data.global);
                this.content.top +=
                    ((local.y - this.pointerDown.last.y) / (this.boxHeight - this.verticalHandle.height)) *
                    (this.scrollHeight - this.boxHeight);
                this.pointerDown.last = local;
            }

            this.moved();

            if (this.options.stopPropagation) {
                e.stopPropagation();
            }
        }
    }

    /**
     * Handle pointer down on content or scrollbar.
     */
    private dragEnd() {
        this.pointerDown = null;
    }

    private dragStart() {
        this.pointerDown = {};
    }

    /**
     * If a child is not visible the object will not be drawn, and the updateTransform function will not be called.
     */
    updateVisibleChildren() {
        const bounds = this.getVisibleBounds();
        this.content.children.forEach(child => {
            const isVisible = child.visible;
            const newVisibility = this.isChildVisible(child, bounds);
            if (isVisible !== newVisibility) {
                child.visible = newVisibility;
                child.emit(ScrollBoxEvent.VISIBILITY_CHANGED, newVisibility);
            }
        });
    }

    /**
     * Return true if the child is visible otherwise false.
     */
    protected isChildVisible(child: DisplayObject, bounds?: any) {
        if (!bounds) bounds = this.getVisibleBounds();
        const childBounds = child.getLocalBounds();

        return (
            child.x >= bounds.x - childBounds.width &&
            child.x <= bounds.x + bounds.width &&
            child.y >= bounds.y - childBounds.height &&
            child.y <= bounds.y + bounds.height
        );
    }
    /**
     * Resize the mask for the container.
     */
    public resize(scrollWidth: number, scrollHeight: number, boxWidth?: number, boxHeight?: number) {
        this.options.boxWidth = boxWidth ?? this.options.boxWidth;
        this.options.boxHeight = boxHeight ?? this.options.boxHeight;
        if (scrollWidth) {
            this.scrollWidth = scrollWidth;
        }
        if (scrollHeight) {
            this.scrollHeight = scrollHeight;
        }
        this.content.resize(this.options.boxWidth, this.options.boxHeight, this.scrollWidth, this.scrollHeight);
        this.update();
    }

    /**
     * Return the visible bounds.
     */
    public getVisibleBounds() {
        const bounds = this.content.getVisibleBounds();
        bounds.x -= this.options.horizontalOffset.left;
        bounds.width += this.options.horizontalOffset.left + this.options.horizontalOffset.right;
        bounds.y -= this.options.verticalOffset.top;
        bounds.height += this.options.verticalOffset.top + this.options.verticalOffset.bottom;
        return bounds;
    }
}
